import { Fragment, memo, useMemo, useState } from "react";
import { OrderReceipt, PageProps } from "../../components/types";
import PageContainer from "../PageContainer";
import { FcApproval } from "react-icons/fc";
import { FaRegUserCircle } from "react-icons/fa";
import { HiX } from 'react-icons/hi';
import { Badge, BadgeVariations, SelectField } from '@aws-amplify/ui-react';
import { Menu, Transition } from '@headlessui/react'
import DataGrid, { SortColumn } from "react-data-grid";
import useOrderReceipt from "../../hooks/useOrderReceip";
import { OrderReceiptApprovalColumns } from "../../constants/Column";

import { Department, OrderType } from "../../models";
import { IoIosCalendar } from "react-icons/io";
import { IoFilterOutline } from "react-icons/io5";
import { MdOutlineCheckBox, MdOutlineCheckBoxOutlineBlank } from "react-icons/md";
import DatePicker from 'react-datepicker'
import SearchBox from "../../components/SearchBox";
import { formatDateObject } from "../../utilities/Format";
import { groupBy, sumBy, map } from "lodash";
import ExportAsButton from "../../components/ExportAsButton";

import { writeFile, utils } from 'xlsx';

export enum RECEIPT_STATUSES {
  WAIT_FOR_APPROVE = 'WAIT FOR APPROVE',
  APPROVED = 'APPROVED',
  // ALL = 'ALL'
}
type Comparator = (a: OrderReceipt, b: OrderReceipt) => number;
const getComparator= (sortColumn: string): Comparator => {
  switch (sortColumn) {
    case 'productionOrderId':
      return (a, b) => {
        return a[sortColumn].localeCompare(b[sortColumn]);
      };
    case 'orderMaterialCode':
    case 'approvedByPdBy':
    case 'approvedByQcBy':
    case 'approvedByWhBy':
    case 'approvedByFaBy':
      return (a, b) => {
        return (a[sortColumn] || '').localeCompare(b[sortColumn]!);
      };
    case 'approvedByPdDatetime':
    case 'approvedByQcDatetime':
    case 'approvedByWhDatetime':
    case 'approvedByFaDatetime':
    case 'orderQty':
    case 'totalAmount':
    case 'retainedIssue':
    case 'reject':
    case 'otherIssue':
      return (a, b) => {
        return a[sortColumn] === b[sortColumn] ? 0 : a[sortColumn] ? 1 : -1;
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

const OrderReceiptApproval = ({ title, department, from, to }: PageProps) => {
  let badegeDepVariation : BadgeVariations = 'success';
  if (department === 'QC') badegeDepVariation = 'error';
  if (department === 'Warehouse') badegeDepVariation = 'warning';
  if (department === 'FA') badegeDepVariation = 'info';

  const [ dateRange, setDateRange ] = useState<[Date | null, Date | null]>([
    from || new Date(),
    to || new Date()
  ]);
  const [ startDate, endDate ] = dateRange;
  
  const [ currentStatus, setCurrentStatus ] = useState<RECEIPT_STATUSES>(RECEIPT_STATUSES.WAIT_FOR_APPROVE);
  const [ keyword, setKeyword ] = useState<string>('')
  const [ currentOrderType, setCurrentOrderType ] = useState<OrderType>()
  const [ sortColumns, setSortColumns ] = useState<readonly SortColumn[]>([]);
  
  const badgeOfOrderTypeVariation = useMemo(() => {
    let variation : BadgeVariations = 'warning'
    if (currentOrderType === OrderType.REPACK) variation = 'info';
    if (currentOrderType === OrderType.CLAIM) variation = 'error';
    if (currentOrderType === OrderType.FG) variation = 'success';
    return variation;
  }, [ currentOrderType ])
  
  const orderReceipts = useOrderReceipt(
    startDate, endDate, department as Department, currentStatus, keyword, currentOrderType);

  const groupedOrderReceipts = useMemo(() => {
    const grouped = groupBy(orderReceipts, 'productionOrderId');
    return map(grouped, (receipts) => ({
      ...receipts[0],
      totalAmount: sumBy(receipts, 'totalAmount'),
      retainedIssue: sumBy(receipts, rep => (rep.retainedIssue || 0) + (rep.microIssue || 0) + (rep.validateIssue || 0) + (rep.shelfLifeIssue || 0) + (rep.testIssue || 0)),
      reject: sumBy(receipts, 'reject'),
      otherIssue: sumBy(receipts, 'otherIssue')
    }));
  }, [ orderReceipts ]);

  const sortedRows = useMemo((): readonly OrderReceipt[] => {
    if (sortColumns.length === 0) return groupedOrderReceipts;

    return [...groupedOrderReceipts].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [ groupedOrderReceipts, sortColumns ]);

  const exportFile = (type: string) => {
    if (type === 'Excel') {
      const wb = utils.book_new();

      const ws = utils.json_to_sheet(sortedRows.map(row => ({
        'Order No.': row.productionOrderId,
        'Order Type': row.orderType,
        'Material Code': row.orderMaterialCode,
        'Material Description': row.orderMaterialDesc,
        'Lot No.': row.batchLotNo,
        'Bulk Usage': row.bulkUsage,
        'Order Qty': row.orderQty,
        'Actual Qty': row.totalAmount,
        'Avg. Fwt.': row.avgFillingWeight,
        'Total Amount': row.totalAmount,
        'Retained Issue': row.retainedIssue,
        'Micro Issue': row.microIssue,
        'Validate Issue': row.validateIssue,
        'Shelf Life Issue': row.shelfLifeIssue,
        'Test Issue': row.testIssue,
        'Reject': row.reject,
        'Remark': row.remark,
        'Other Issue': row.otherIssue,
        'Other Note': row.otherIssueNote,
        'Approved By PD': row.approvedByPdBy,
        'Approved Datetime': row.approvedByPdDatetime,
        'Approved By QC': row.approvedByQcBy,
        'Approved By QC Datetime': row.approvedByQcDatetime,
        'Approved By WH': row.approvedByWhBy,
        'Approved By WH Datetime': row.approvedByWhDatetime,
        'Approved By FA': row.approvedByFaBy,
        'Approved By FA Datetime': row.approvedByFaDatetime
      })))
      utils.book_append_sheet(wb, ws, 'Order Receipt');

      const formattedDateStart = formatDateObject(dateRange[0])
      const formattedDateEnd = formatDateObject(dateRange[1])
      const exportDates = formattedDateStart === formattedDateEnd ? 
        formattedDateStart : `${formattedDateStart}_${formattedDateEnd}`
      writeFile(wb, `order_receipt_${currentStatus}_${exportDates}.xlsx`);
    }
  }

  return (
    <PageContainer
      title={title}
      className="space-y-3 "
      icon={<FcApproval className="text-3xl" />}
      rightElement={<Badge variation={badegeDepVariation}><FaRegUserCircle className="mr-1"/>{department}</Badge>}
      withoutContainer={true}
    >
      <div className="flex flex-col justify-between gap-3 md:flex-row">
        <div className='flex gap-2'>
          {[RECEIPT_STATUSES.WAIT_FOR_APPROVE, RECEIPT_STATUSES.APPROVED].map(sts => 
            <button key={sts} title={sts}
              className={` w-full md:w-fit text-sm p-2 px-4 text-center rounded-full border${currentStatus === sts ? ' bg-gray-500 font-bold text-white':' border-gray-500 font-light'}`}
              onClick={() => setCurrentStatus(sts)}
              >
              {sts === RECEIPT_STATUSES.WAIT_FOR_APPROVE && <MdOutlineCheckBoxOutlineBlank className="inline-block mr-1" size={18} />}
              {sts === RECEIPT_STATUSES.APPROVED && <MdOutlineCheckBox className="inline-block mr-1" size={18} />}
              {sts}
            </button>
          )}
        </div>
        <div className="flex w-full gap-2 md:w-fit">
          <SearchBox placeholder='PD Order No...'
            className="w-full md:w-fit"
            value={keyword}
            onChanged={keyword => setKeyword(keyword)}
            isRound={false}
            fullHeight={true}
          />
          <Menu as="div" className="relative inline-block w-full text-left md:w-fit" >
            <div >
              <Menu.Button className="inline-flex justify-center items-center w-full px-4 py-2 text-sm font-medium text-header border border-[#CCCCCC] bg-white rounded-md hover:bg-header/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75">
                <IoFilterOutline
                  className="w-5 h-5 mr-2 -ml-1 text-header"
                  aria-hidden="true"
                />
                Filters<div className='flex flex-wrap gap-1 ml-1'>
                  <Badge size='small'>{formatDateObject(startDate, 'dd/MM/yyyy')} - {formatDateObject(endDate, 'dd/MM/yyyy')}</Badge>
                  {currentOrderType && <Badge size='small' variation={badgeOfOrderTypeVariation} >
                    {currentOrderType}
                    <div 
                      role='button'
                      title="remove_filter_type"
                      className="ml-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentOrderType(undefined)
                      }}>
                      <HiX />
                    </div>
                  </Badge>}
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
              >
              <Menu.Items className="absolute right-0 z-10 pt-3 pb-1 origin-top-right bg-white divide-y divide-gray-100 rounded-md shadow-lg w-72 ring-1 ring-black/5 focus:outline-none">
                <h6 className="flex items-center gap-2 px-5 font-bold"><IoFilterOutline size={16} />Filters</h6>
                <div className="px-5 py-3 ">
                <p className="text-sm text-muted">เลือกช่วงเวลา</p>
                  <div className="relative">
                    <span className='absolute inset-y-0 left-0 z-10 flex items-center px-3 text-muted'>
                      <IoIosCalendar />
                    </span>
                    <DatePicker
                      selectsRange={true}
                      className='mr-5 pl-9 input-form-date py-[0.5rem]'
                      onChange={dates => setDateRange(dates)}
                      startDate={startDate}
                      endDate={endDate}
                      dateFormat="dd/MM/yyyy"
                    />
                  </div>
                </div>
                <div className="px-5 py-3 ">
                  <SelectField
                    label="ชนิดของ Order"
                    descriptiveText="เพื่อกรองข้อมูลเช่น REPACK, CLAIM"
                    placeholder="เลือกชนิดของ Order"
                    value={currentOrderType}
                    onChange={(e) => setCurrentOrderType(e.target.value as OrderType)}
                  >
                    {Object.keys(OrderType).map(sts => <option key={sts} value={sts}>{sts}</option>)}
                  </SelectField>
                </div>
              </Menu.Items>
            </Transition>
          </Menu>
          <ExportAsButton
            onExportAs={exportFile}
          />
        </div>
      </div>
      <DataGrid
        rows={sortedRows}
        columns={OrderReceiptApprovalColumns}
        style={{ height: "100%", contain: "none" }}
        defaultColumnOptions={{ resizable: true, sortable: true }}
        rowHeight={55}
        sortColumns={sortColumns}
        onSortColumnsChange={setSortColumns}
      />
    </PageContainer>
  );
}

export default memo(OrderReceiptApproval);